import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { toast } from "sonner";

interface TimeSlotData {
  item_id: string;
  day_of_week?: number; // 0-6 (Sunday-Saturday) - for recurring slots
  specific_date?: string; // YYYY-MM-DD - for specific date slots
  start_time: string;
  end_time: string;
  is_available?: boolean;
}

export const useManageTimeSlots = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  const createTimeSlot = useMutation({
    mutationFn: async (data: TimeSlotData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      // Get item and its category information
      const { data: item, error: itemError } = await supabase
        .from('reservation_items')
        .select('category_id')
        .eq('id', data.item_id)
        .single();

      console.log('🔍 [useManageTimeSlots] Item data:', item, 'Error:', itemError);

      if (itemError || !item) throw new Error("Elemento no encontrado");

      // Get category information to verify gym_id
      const { data: category, error: categoryError } = await supabase
        .from('reservation_categories')
        .select('gym_id')
        .eq('id', item.category_id)
        .single();

      console.log('🏢 [useManageTimeSlots] Category data:', category, 'Error:', categoryError);

      if (categoryError || !category) throw new Error("Categoría no encontrada");

      const gymId = category.gym_id;
      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', gymId)
        .single();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para gestionar este gimnasio");
      }

      // Validate that either day_of_week OR specific_date is provided
      if (!data.day_of_week && !data.specific_date) {
        throw new Error("Debe especificar día de la semana o fecha específica");
      }
      if (data.day_of_week && data.specific_date) {
        throw new Error("Solo puede especificar día de la semana O fecha específica, no ambos");
      }

      // Check for overlapping time slots
      let query = supabase
        .from('reservation_time_slots')
        .select('*')
        .eq('item_id', data.item_id)
        .or(`start_time.lt.${data.end_time},end_time.gt.${data.start_time}`);

      if (data.day_of_week) {
        query = query.eq('day_of_week', data.day_of_week);
      } else if (data.specific_date) {
        query = query.eq('specific_date', data.specific_date);
      }

      const { data: existing } = await query;

      if (existing && existing.length > 0) {
        throw new Error("Ya existe un horario que se superpone con el seleccionado");
      }

      // Create the insert object with proper null handling
      const insertData: any = {
        item_id: data.item_id,
        start_time: data.start_time,
        end_time: data.end_time,
        is_available: data.is_available ?? true,
      };

      if (data.day_of_week !== undefined) {
        insertData.day_of_week = data.day_of_week;
        insertData.specific_date = null;
      } else if (data.specific_date) {
        insertData.specific_date = data.specific_date;
        insertData.day_of_week = null;
      }

      const { data: timeSlot, error } = await supabase
        .from('reservation_time_slots')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return timeSlot;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-time-slots'] });
      toast.success("Horario creado exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear el horario");
    },
  });

  const deleteTimeSlot = useMutation({
    mutationFn: async (timeSlotId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      const { error } = await supabase
        .from('reservation_time_slots')
        .delete()
        .eq('id', timeSlotId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-time-slots'] });
      toast.success("Horario eliminado exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar el horario");
    },
  });

  return {
    createTimeSlot,
    deleteTimeSlot,
  };
};